import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HomeOutlined,
  UsergroupAddOutlined,
  ShopOutlined,
  UserOutlined,
  DownOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
} from "@ant-design/icons";

interface SidebarProps {
  isOpen: boolean;
  isLocked?: boolean;   // true = desktop push mode, false = mobile overlay mode
  onClose: () => void;
  activeMenu: string;
  onMenuSelect: (menuId: string) => void;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  isLocked = false,
  onClose,
  onMenuSelect,
  onToggle,
}) => {
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const menuItems = [
    { id: "tableau de bord",   label: "Tableau de bord",    icon: <HomeOutlined />,         path: "/accueil" },
    ...(user?.roles?.includes("ROLE_ADMIN") ? [
      { id: "gestion-user",    label: "Utilisateurs",        icon: <UsergroupAddOutlined />, path: "/utilisateurs" },
    ] : []),
    { id: "gestion-websites",  label: "Gestion des fiches",  icon: <ShopOutlined />,         path: "/sites-web" },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleClick = (path: string, menuId: string) => {
    onMenuSelect(menuId);
    navigate(path);
    if (!isLocked) onClose(); // close overlay on mobile after nav
  };

  const handleLogout = () => { logout(); navigate("/auth"); };

  // Close dropdown on outside click
  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [dropdownOpen]);

  return (
    <>
      {/* ── Mobile overlay backdrop ───────────────────────────────────────── */}
      {isOpen && !isLocked && (
        <div
          className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-[999] transition-opacity lg:hidden"
          onClick={onClose}
        />
      )}

      {/* ── Sidebar panel ────────────────────────────────────────────────── */}
      <div
        className={`
          fixed top-0 left-0 h-screen w-64 z-[1000]
          bg-white border-r border-slate-100
          flex flex-col
          shadow-sm
          transition-transform duration-300 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-5 border-b border-slate-100 flex-shrink-0">
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[13px] font-bold text-slate-800 tracking-tight truncate">
              Google Business
            </span>
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
              Profile Manager
            </span>
          </div>
          <button
            onClick={onToggle}
            className="text-slate-400 hover:text-[#0F52BA] bg-slate-50 hover:bg-blue-50
              p-1.5 rounded-lg transition-colors border-none cursor-pointer
              flex items-center justify-center flex-shrink-0"
            aria-label="Réduire le menu"
          >
            <MenuFoldOutlined style={{ fontSize: 17 }} />
          </button>
        </div>

        {/* ── Nav items ────────────────────────────────────────────────── */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-1">
          {menuItems.map((item) => {
            const active = isActive(item.path);
            return (
              <button
                key={item.id}
                onClick={() => handleClick(item.path, item.id)}
                className={`
                  flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm
                  border-none cursor-pointer text-left
                  transition-all duration-150
                  ${active
                    ? "text-[#0F52BA] bg-blue-50 font-semibold"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50 font-medium"
                  }
                `}
              >
                <span className={`text-base flex items-center justify-center flex-shrink-0 transition-colors
                  ${active ? "text-[#0F52BA]" : "text-slate-400"}`}>
                  {item.icon}
                </span>
                <span className="truncate">{item.label}</span>
                {active && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#0F52BA] flex-shrink-0" />
                )}
              </button>
            );
          })}
        </nav>

        {/* ── Profile section ──────────────────────────────────────────── */}
        <div
          className="flex-shrink-0 px-3 py-3 border-t border-slate-100 relative"
          ref={dropdownRef}
        >
          {/* Dropdown */}
          {dropdownOpen && (
            <div className="absolute bottom-[76px] left-3 right-3 bg-white rounded-2xl
              shadow-xl border border-slate-100 overflow-hidden
              animate-[gbpFadeIn_0.18s_ease]">
              <div className="px-4 py-3 bg-slate-50/60 border-b border-slate-100">
                <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-0.5">Compte</p>
                <p className="text-xs text-slate-600 truncate font-medium">{user?.email}</p>
              </div>
              <button
                onClick={() => { setDropdownOpen(false); navigate("/profile"); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700
                  hover:bg-slate-50 transition-colors border-none cursor-pointer"
              >
                <UserOutlined className="text-[#0F52BA]" />
                Mon Profil
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-500
                  hover:bg-rose-50 transition-colors border-none cursor-pointer"
              >
                <LogoutOutlined />
                Déconnexion
              </button>
            </div>
          )}

          {/* Profile trigger */}
          <button
            onClick={() => setDropdownOpen((p) => !p)}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl
              transition-all duration-150 border-none cursor-pointer
              ${dropdownOpen ? "bg-slate-100" : "hover:bg-slate-50"}`}
          >
            <div className="w-8 h-8 rounded-full bg-blue-50 border border-blue-100
              flex items-center justify-center text-[#0F52BA] text-xs font-bold flex-shrink-0">
              {user?.nom?.charAt(0).toUpperCase() || <UserOutlined />}
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-[13px] font-bold text-slate-800 truncate leading-tight">
                {user?.nom || "Utilisateur"}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <p className="text-[10px] text-slate-400 font-medium">En ligne</p>
              </div>
            </div>
            <DownOutlined
              className={`text-[10px] text-slate-400 flex-shrink-0
                transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;